import type { DirectiveBinding, ObjectDirective } from 'vue';

export interface ResizableTextareaOptions {
  /** 实际需要调整高度的子元素选择器，默认查找 Element Plus textarea。 */
  target?: string;
  minHeight?: number;
  maxHeight?: number;
  disabled?: boolean;
}

interface ResizeState {
  handle: HTMLDivElement;
  target: HTMLElement;
  options: ResizableTextareaOptions;
  startY: number;
  startHeight: number;
  currentHeight?: number;
  resizeFrame?: number;
  resize: (event: PointerEvent) => void;
  keepManualHeight: () => void;
  stop: () => void;
}

const states = new WeakMap<HTMLElement, ResizeState>();

function normalizeOptions(binding: DirectiveBinding<ResizableTextareaOptions | undefined>) {
  return binding.value ?? {};
}

function removeDocumentListeners(state: ResizeState) {
  document.removeEventListener('pointermove', state.resize);
  document.removeEventListener('pointerup', state.stop);
  document.removeEventListener('pointercancel', state.stop);
}

function mount(el: HTMLElement, binding: DirectiveBinding<ResizableTextareaOptions | undefined>) {
  const options = normalizeOptions(binding);
  const target = options.target
    ? el.querySelector<HTMLElement>(options.target)
    : el.querySelector<HTMLElement>('.el-textarea__inner');

  if (!target) return;

  const handle = document.createElement('div');
  handle.className = 'resizable-textarea-handle';
  handle.title = '拖拽调整高度';
  handle.setAttribute('aria-hidden', 'true');
  const icon = document.createElement('span');
  icon.className = 'resizable-textarea-handle__icon';
  icon.textContent = '↕';
  handle.appendChild(icon);

  const state: ResizeState = {
    handle,
    target,
    options,
    startY: 0,
    startHeight: 0,
    resize: () => undefined,
    keepManualHeight: () => undefined,
    stop: () => undefined,
  };

  state.keepManualHeight = () => {
    if (state.currentHeight === undefined) return;
    if (state.resizeFrame !== undefined) cancelAnimationFrame(state.resizeFrame);
    state.resizeFrame = requestAnimationFrame(() => {
      target.style.height = `${state.currentHeight}px`;
    });
  };

  state.resize = (event) => {
    const minHeight = state.options.minHeight ?? 48;
    const maxHeight = state.options.maxHeight ?? 600;
    const nextHeight = state.startHeight + event.clientY - state.startY;
    state.currentHeight = Math.min(maxHeight, Math.max(minHeight, nextHeight));
    state.target.style.height = `${state.currentHeight}px`;
    state.target.style.overflowY = 'auto';
  };

  state.stop = () => {
    el.classList.remove('is-textarea-resizing');
    document.body.classList.remove('is-textarea-resizing');
    removeDocumentListeners(state);
  };

  handle.addEventListener('pointerdown', (event) => {
    if (state.options.disabled || el.classList.contains('is-disabled')) return;
    event.preventDefault();
    state.startY = event.clientY;
    state.startHeight = target.getBoundingClientRect().height;
    el.classList.add('is-textarea-resizing');
    document.body.classList.add('is-textarea-resizing');
    document.addEventListener('pointermove', state.resize);
    document.addEventListener('pointerup', state.stop);
    document.addEventListener('pointercancel', state.stop);
  });

  el.classList.add('is-bottom-resizable-textarea');
  el.classList.toggle('is-resize-disabled', Boolean(options.disabled));
  el.appendChild(handle);
  target.addEventListener('input', state.keepManualHeight);
  states.set(el, state);
}

function update(el: HTMLElement, binding: DirectiveBinding<ResizableTextareaOptions | undefined>) {
  const state = states.get(el);
  if (!state) return;
  state.options = normalizeOptions(binding);
  el.classList.toggle('is-resize-disabled', Boolean(state.options.disabled));
}

function unmount(el: HTMLElement) {
  const state = states.get(el);
  if (!state) return;
  state.stop();
  state.target.removeEventListener('input', state.keepManualHeight);
  if (state.resizeFrame !== undefined) cancelAnimationFrame(state.resizeFrame);
  state.handle.remove();
  states.delete(el);
}

export const vResizableTextarea: ObjectDirective<
  HTMLElement,
  ResizableTextareaOptions | undefined
> = {
  mounted: mount,
  updated: update,
  beforeUnmount: unmount,
};
